import { PortfolioData, HOLDING_TYPES } from './portfolios';
import { PERMISSIONS } from './permissions';
import Shelf from '@/types/shelf';
import PortfolioHolding from '@/types/portfolio-holding';
import Fund from '@/types/fund';
import Stock from '@/types/stock';

export const COMPLIANT_INSTRUMENT = 'COMPLIANT';

export enum ShelfStatus {
  ON_SHELF = 'shelf_onShelf',
  ON_SHELF_KYP_COMPLETE = 'shelf_onShelfKYPComplete',
  ON_SHELF_KYP_PARTIALLY_COMPLETE = 'shelf_onShelfKYPPartiallyComplete',
  PARTIALLY_ON_SHELF = 'shelf_partiallyOnShelf',
}

export const statusesConfig = {
  [ShelfStatus.ON_SHELF]: {
    key: ShelfStatus.ON_SHELF,
    permissions: [PERMISSIONS.SHELF_INFORMATION],
  },
  [ShelfStatus.ON_SHELF_KYP_COMPLETE]: {
    key: ShelfStatus.ON_SHELF_KYP_COMPLETE,
    permissions: [PERMISSIONS.KYP],
  },
  [ShelfStatus.ON_SHELF_KYP_PARTIALLY_COMPLETE]: {
    key: ShelfStatus.ON_SHELF_KYP_PARTIALLY_COMPLETE,
    permissions: [PERMISSIONS.KYP],
  },
  [ShelfStatus.PARTIALLY_ON_SHELF]: {
    key: ShelfStatus.PARTIALLY_ON_SHELF,
    permissions: [PERMISSIONS.SHELF_INFORMATION],
  },
};

export const isOnShelf = (instrumentShelves: Shelf[] | undefined, predicate?: (...args: any[]) => boolean) =>
  !!(instrumentShelves && instrumentShelves.length > 0 && instrumentShelves.some(predicate || (() => true)));

export const convertShelvesToShelfStatuses = (
  instrumentShelves: Shelf[] | undefined,
  complianceComparisons?: string[],
  predicate?: (...args: any[]) => boolean,
) => {
  const onShelf = isOnShelf(instrumentShelves, predicate);

  if (onShelf && complianceComparisons && complianceComparisons.length > 0) {
    return [ShelfStatus.ON_SHELF_KYP_COMPLETE];
  }

  if (onShelf) {
    return [ShelfStatus.ON_SHELF];
  }

  return [];
};

export const convertPortfolioHoldingsShelvesToShelfStatuses = (
  holdingsShelves: Shelf[][],
  holdingsComplianceComparisons?: string[][],
  predicate?: (...args: any[]) => boolean,
) => {
  const statuses: ShelfStatus[] = [];

  const onShelf = holdingsShelves.length > 0 && holdingsShelves.every(holdingShelves => isOnShelf(holdingShelves, predicate));
  const complianceCompleteCount =
    (holdingsComplianceComparisons &&
      holdingsComplianceComparisons.reduce((acc, holdingsComplianceComparison) => {
        if (holdingsComplianceComparison.length > 0) {
          return acc + 1;
        }

        return acc;
      }, 0)) ||
    0;

  if (onShelf && !holdingsComplianceComparisons) {
    statuses.push(ShelfStatus.ON_SHELF);
  }

  if (onShelf && complianceCompleteCount === holdingsShelves.length) {
    statuses.push(ShelfStatus.ON_SHELF_KYP_COMPLETE);
  }

  if (complianceCompleteCount < holdingsShelves.length) {
    statuses.push(ShelfStatus.ON_SHELF_KYP_PARTIALLY_COMPLETE);
  }

  if (!onShelf) {
    statuses.push(ShelfStatus.PARTIALLY_ON_SHELF);
  }

  return statuses;
};

export const addShelvesToPortfolioHoldings = (holdings: PortfolioHolding[], portfolioData?: PortfolioData) => {
  if (holdings.length > 0 && portfolioData) {
    return holdings.map(holding => {
      let storeKey;
      switch (holding.original.type) {
        case HOLDING_TYPES.FUND:
        case HOLDING_TYPES.CUSTOM_YIELD:
          storeKey = 'funds';
          break;
        case HOLDING_TYPES.STOCK:
          storeKey = 'stocks';
          break;
      }
      const storeHoldingData = (typeof storeKey !== 'undefined' && portfolioData[storeKey as keyof typeof portfolioData]) || [];
      const storeHolding = (storeHoldingData as (Fund | Stock)[]).find((entity: Fund | Stock) => entity._id === holding.original.id);

      if (storeHolding) {
        return {
          ...holding,
          filteredShelves: storeHolding.filteredShelves,
          complianceComparisons: storeHolding.complianceComparisons,
        };
      }

      return holding;
    });
  }

  return holdings;
};
